'use client';

import Link from 'next/link';

import styles from './Privacy.module.css';

interface LegalBasisModalProps {
  closeModal: () => void;
}

export const LegalBasisModal = ({ closeModal }: LegalBasisModalProps) => {
  return (
    <div className={styles.legalBasisModal}>
      <h1>Legal bases explained</h1>
      <ul>
        <li>
          <p>
            <strong>Consent:</strong> We will process your personal information for promotional or
            marketing purposes where you have given us your express consent for us to do this, where
            required by law. This includes sending you alerts about your saved searches.
          </p>
          <p>
            <strong>You can opt-out of further marketing at any time</strong> by selecting the
            “unsubscribe” link at the end of all our marketing and promotional update communications
            to you, or <strong>by sending us an email at</strong>{' '}
            <a href="mailto:privacy@drive.co.uk" target="_blank" rel="noopener">
              privacy@drive.co.uk
            </a>
            .
          </p>
          <p>
            For more information, please refer to{' '}
            <Link href="#promotional-updates" onClick={closeModal}>
              Our Promotional Updates and Communication
            </Link>{' '}
            section.
          </p>
          <p>
            We also rely on consent for some of the cookies we use (see our{' '}
            <Link href="/cookies" target="_blank">
              Cookies Policy
            </Link>{' '}
            for more detail).
          </p>
        </li>
        <li>
          <strong>Contract:</strong> We collect, store and process your personal information where
          it&apos;s necessary for performing a contract you have with us (such as our{' '}
          <Link href="/terms-conditions" onClick={closeModal}>
            Terms and Conditions, any applicable terms for a prize draw, competition, or other
            promotion
          </Link>{' '}
          or where you have asked us to take specific steps before entering into that contract).
        </li>
        <li>
          <p>
            <strong>Legal Obligation:</strong> We may need to process your personal information to
            comply with our legal obligations, including under applicable local, UK and EU law
            (including Data Protection Law and any licensing requirements), and/or any court orders.
          </p>
          <p>
            If we run a prize draw, competition or other promotion we may need to publish limited
            personal information to demonstrate that an award took place. If you have any questions
            about this, please contact us at{' '}
            <a href="mailto:privacy@drive.co.uk" target="_blank" rel="noopener">
              privacy@drive.co.uk
            </a>
            .
          </p>
        </li>
        <li>
          <p>
            <strong>Legitimate interests:</strong> processing your personal information is sometimes
            necessary for us to do the following activities, in our own interest or sometimes in the
            interests of a third party, like dealers we work with.
          </p>
          <p>Our legitimate interests are:</p>
          <ul>
            <li>Gaining insights on how you use our Service. </li>
            <li>Delivering, developing and improving our Service.</li>
            <li>
              Enabling us to enhance, customise or modify our Service, service and communications.
            </li>
            <li>Growing our business and informing our marketing strategy.</li>
            <li>Keeping our records updated.</li>
            <li>Improving our Service and data security. </li>
            <li>
              Enforcing or applying our terms or other agreements with you, including recovering
              fees or other debt due to us.
            </li>
            <li>
              Complying with and enforcing our agreements with dealers, financing providers (like
              VWFS) and other third parties, including sharing necessary information and recovering
              fees or other debt due to us.
            </li>
            <li>
              Providing you with our{' '}
              <Link href="#promotional-updates" onClick={closeModal}>
                Promotional Updates and Communications
              </Link>{' '}
              if you work for one of our dealership partners, or are otherwise interacting with us
              in a business context, or in certain cases where you have purchased a service from us
              and have not opted-out at the time of purchase or any time since (you are free to
              opt-out at any time!).
            </li>
          </ul>
          <p>
            In each case, we balance our legitimate interests with your rights and interests. If you
            would like further information about how we assess our legitimate interests, please
            contact us at{' '}
            <a href="mailto:privacy@drive.co.uk" target="_blank" rel="noopener">
              privacy@drive.co.uk
            </a>
            .
          </p>
        </li>
        <li>
          We also use different types of cookies (including tracking pixels and other automatically
          collected information) on our Service with your <strong>consent</strong> – we explain this
          in the{' '}
          <Link href="/cookies" target="_blank">
            Cookies Policy
          </Link>
          .{' '}
        </li>
      </ul>
      <p>
        Please{' '}
        <a href="mailto:privacy@drive.co.uk" target="_blank" rel="noopener">
          contact us
        </a>{' '}
        if you would like further details about the specific legal basis we are relying on to
        process your personal information.
      </p>
    </div>
  );
};
